import { Flex, FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import { logAutoCompleteSuggestionAcceptance } from "@/components/FullPageEditor/components/menus/GhostTextMenu/utils";
import FlashcardEditor from "@/components/flashcards/flashcardsCard/EditableFlashcardCard/FlashcardEditor";
import {
    ImageButton,
    RecordButton,
} from "@/components/flashcards/flashcardsCard/EditableFlashcardCard/SharedComponents";
import { useUpgradePopupContextSelector } from "@/features/Payments/PaywallPopup/UpgradePopupContext";
import ResponsiveDialog from "@/features/ResponsiveDialog/ResponsiveDialog";
import DialogCloseBtn from "@/features/ResponsiveDialog/styled/DialogCloseBtn";
import { useBreakPoints } from "@/hooks/styles/useBreakpoints";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { Editor } from "@knowt/editor/react";
import { AICompletionType, Flashcard, FlashcardSide, ItemType, UpgradeEvent } from "@knowt/syncing/graphql/schema";
import { FlashcardAutocompleteType } from "@knowt/syncing/hooks/ai/constants";
import { getAIFlashcardsContext, useAICopilot } from "@knowt/syncing/hooks/ai/useAICopilot";
import { FLASHCARD_LANGUAGES } from "@knowt/syncing/hooks/flashcards/languageUtils";
import { useFlashcardSet } from "@knowt/syncing/hooks/flashcards/useFlashcards";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { objectWithout } from "@knowt/syncing/utils/dataCleaning";
import useCombinedState from "@knowt/syncing/utils/hooks/useCombinedState";
import { Lightbulb, Settings2Icon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import LazyLoaded from "../LazyLoaded";
import CustomHintPopup from "../flashcards/flashcardsCard/EditableFlashcardCard/CustomHintPopup";
import FlashcardEditorToolbar from "../flashcards/flashcardsCard/EditableFlashcardCard/FlashcardEditorToolbar";
import AudioInputSection from "../flashcards/flashcardsCard/components/AudioInputSection";
import CustomMcOptionsInputSection from "../flashcards/flashcardsCard/components/CustomMcOptionsInputSection";
import CustomizationFeatureButton from "../flashcards/flashcardsCard/components/CustomizationFeatureButton";

const EditableFlashcardPopup = ({
    isOpen,
    onClose,
    flashcard: initialFlashcard,
    isCustomizationFeaturesBlocked,
}: {
    flashcard: Flashcard & { side?: FlashcardSide };
    isOpen: boolean;
    onClose: () => void;
    isCustomizationFeaturesBlocked?: boolean;
}) => {
    const { mdDown } = useBreakPoints();
    const { user } = useCurrentUser();

    const [localFlashcard, _setLocalFlashcard] = useState<Flashcard>(initialFlashcard);

    const [isDirty, setIsDirty] = useState(false);

    const setLocalFlashcard = useCallback((flashcard: Flashcard, isInitializationPhase = false) => {
        if (!isInitializationPhase) {
            setIsDirty(true);
        }
        _setLocalFlashcard(flashcard);
    }, []);

    useEffect(() => {
        setLocalFlashcard(objectWithout(initialFlashcard, "side"), true);
    }, [initialFlashcard, setLocalFlashcard]);

    const { flashcardId } = localFlashcard;

    const openUpgradePopup = useUpgradePopupContextSelector(state => state.openUpgradePopup);

    const { flashcardSet, updateFlashcard, updateFlashcardSetMetaData, commitUpdates } = useFlashcardSet({
        flashcardSetId: localFlashcard.flashcardSetId,
        autoCommit: false,
    });

    const termInputRef = useRef<Editor>(undefined);
    const definitionInputRef = useRef<Editor>(undefined);

    const [focusedEditor, setFocusedEditor] = useState<Editor | null>(null);

    const [recordingSide, setRecordingSide] = useState<FlashcardSide | null>(null);

    const [isBottomSectionVisible, setIsBottomSectionVisible] = useState(false);

    const [isShowingAudioSection, setIsShowingAudioSection] = useState(false);
    const [isShowingCustomMCSection, setIsShowingCustomMCSection] = useState(false);
    const [isCustomHintPopupOpen, setIsCustomHintPopupOpen] = useState(false);

    const [localMcOptions, setLocalMcOptions] = useState(localFlashcard?.mcOptions);
    const [localTermCustomHint, setLocalTermCustomHint] = useState(localFlashcard?.termCustomHint);
    const [localDefinitionCustomHint, setLocalDefinitionCustomHint] = useState(localFlashcard?.definitionCustomHint);

    const termLanguage = useMemo(() => flashcardSet?.termLanguage, [flashcardSet?.termLanguage]);
    const definitionLanguage = useMemo(() => flashcardSet?.definitionLanguage, [flashcardSet?.definitionLanguage]);

    const [currInputFocus, updateCurrInputFocus] = useCombinedState({ term: false, definition: false });

    const isInputFocused = useMemo(() => currInputFocus.term || currInputFocus.definition, [currInputFocus]);

    const { getSuggestion } = useAICopilot({
        itemType: ItemType.FLASHCARDSET,
        itemId: flashcardSet?.flashcardSetId as string,
    });

    const save = async () => {
        setIsDirty(false);
        await updateFlashcard(flashcardId, localFlashcard);
        await commitUpdates();
    };

    const handleClose = async () => {
        setIsShowingAudioSection(false);
        setIsShowingCustomMCSection(false);
        setRecordingSide(null);

        onClose();
        if (!isDirty) return;
        setTimeout(() => save(), 300); // without timeout it could block the popup close animation
    };

    // biome-ignore lint: useExhaustiveDependencies
    const handleRecordButtonClick = useCallback(
        (side: FlashcardSide) => {
            if (isCustomizationFeaturesBlocked) {
                if (!localFlashcard?.flashcardSetId) return;

                return openUpgradePopup({
                    event: UpgradeEvent.CUSTOM_FLASHCARD_AUDIO,
                    context: { itemId: localFlashcard?.flashcardSetId, itemType: ItemType.FLASHCARDSET },
                });
            }

            if (isShowingAudioSection && side === recordingSide) {
                setRecordingSide(null);
                setIsShowingAudioSection(false);
                return;
            }

            setIsShowingCustomMCSection(false);

            setRecordingSide(side);
            setIsShowingAudioSection(true);
        },
        [localFlashcard.flashcardSetId, isShowingAudioSection, recordingSide]
    );

    // biome-ignore lint: useExhaustiveDependencies
    const handleUpdateLocalAudio = useCallback(
        (audio: string, side: FlashcardSide) => {
            if (side === FlashcardSide.TERM) {
                setLocalFlashcard({ ...localFlashcard, termRecordedAudio: audio });
            } else {
                setLocalFlashcard({ ...localFlashcard, definitionRecordedAudio: audio });
            }
        },
        [setLocalFlashcard]
    );

    const showCustomMCSection = () => {
        if (isCustomizationFeaturesBlocked) {
            if (!localFlashcard?.flashcardSetId) return;

            return openUpgradePopup({
                event: UpgradeEvent.CUSTOM_MC,
                context: { itemId: localFlashcard?.flashcardSetId },
            });
        }

        setIsShowingAudioSection(false);
        setRecordingSide(null);
        setIsShowingCustomMCSection(true);
    };

    // biome-ignore lint: useExhaustiveDependencies
    const handleOpenCustomHintsPopup = useCallback(() => {
        if (isCustomizationFeaturesBlocked) {
            if (!localFlashcard?.flashcardSetId) return;

            return openUpgradePopup({
                event: UpgradeEvent.CUSTOM_HINT,
                context: { itemId: localFlashcard?.flashcardSetId, itemType: ItemType.FLASHCARDSET },
            });
        }
        setIsCustomHintPopupOpen(true);
    }, [localFlashcard.flashcardSetId]);

    // biome-ignore lint: useExhaustiveDependencies
    const handleUpdateLocalMcOptions = useCallback(
        (mcOptionsArray: string[] | null) => {
            setLocalMcOptions(mcOptionsArray);
            setLocalFlashcard({
                ...localFlashcard,
                mcOptions: mcOptionsArray ? mcOptionsArray.filter(opt => opt !== "") : null,
            });
        },
        [setLocalFlashcard]
    );

    // biome-ignore lint: useExhaustiveDependencies
    const handleSaveCustomHints = useCallback((termCustomHint: string | null, definitionCustomHint: string | null) => {
        setLocalTermCustomHint(termCustomHint);
        setLocalDefinitionCustomHint(definitionCustomHint);

        setLocalFlashcard({ ...localFlashcard, termCustomHint, definitionCustomHint });
        setIsCustomHintPopupOpen(false);
    }, []);

    useEffect(() => {
        if (isInputFocused) {
            setIsBottomSectionVisible(true);
        } else {
            // A small delay to allow click events to be captured before hiding the section
            const timer = setTimeout(() => setIsBottomSectionVisible(false), 50);
            return () => clearTimeout(timer);
        }
    }, [isInputFocused]);

    useEffect(() => {
        if (
            (recordingSide === FlashcardSide.TERM && currInputFocus.definition) ||
            (recordingSide === FlashcardSide.DEFINITION && currInputFocus.term)
        ) {
            setIsShowingAudioSection(false);
            setRecordingSide(null);
            return;
        }
    }, [recordingSide, currInputFocus]);

    useEffect(() => {
        if (localFlashcard.mcOptions) {
            if (isInputFocused && !isShowingAudioSection && !isCustomizationFeaturesBlocked) {
                setIsShowingCustomMCSection(true);
            }
        }
    }, [isInputFocused, isShowingAudioSection, isCustomizationFeaturesBlocked, localFlashcard.mcOptions]);

    const renderTermSection = () => {
        return (
            <Flex style={{ flexDirection: mdDown ? "column-reverse" : "row", gap: "inherit", width: "100%" }}>
                <FlexColumn style={{ margin: "auto", gap: spacing.XS, alignItems: "stretch" }}>
                    <ImageButton
                        image={localFlashcard.secondaryImage}
                        onImageChange={async ({ image }) =>
                            setLocalFlashcard({ ...localFlashcard, secondaryImage: image })
                        }
                        style={{
                            wordBreak: "normal",
                        }}
                    />
                    <RecordButton
                        onClick={() => handleRecordButtonClick(FlashcardSide.TERM)}
                        tabIndex={-1}
                        inAction={recordingSide === FlashcardSide.TERM}
                        storedAudio={localFlashcard.termRecordedAudio}
                        isBlocked={isCustomizationFeaturesBlocked}
                        style={{
                            wordBreak: "normal",
                        }}
                    />
                </FlexColumn>
                <div style={{ marginTop: "2rem", width: "100%", maxWidth: "70rem" }}>
                    <FlashcardEditor
                        key={localFlashcard.flashcardId}
                        defaultValue={localFlashcard.term || ""}
                        handleInputChange={term => setLocalFlashcard({ ...localFlashcard, term, termAudio: null })}
                        accentProps={{ isAccentButtonsVisible: false }}
                        flashcard={{
                            side: FlashcardSide.TERM,
                            term: {
                                content: localFlashcard.term || "",
                                language: flashcardSet?.termLanguage || FLASHCARD_LANGUAGES.ENGLISH,
                            },
                            definition: {
                                content: localFlashcard.definition || "",
                                language: flashcardSet?.definitionLanguage || FLASHCARD_LANGUAGES.ENGLISH,
                            },
                            onChooseLanguage: selectedLang =>
                                updateFlashcardSetMetaData?.({ definitionLanguage: selectedLang }),
                        }}
                        editorProps={{
                            disableCopilot: true,
                            onCreate: ({ editor }) => {
                                termInputRef.current = editor;
                            },
                            onFocus: ({ editor }) => {
                                setFocusedEditor(editor);
                                updateCurrInputFocus({ term: true });
                            },
                            onBlur: () => {
                                setFocusedEditor(null);
                                updateCurrInputFocus({ term: false });
                            },
                        }}
                        handleImagePaste={secondaryImage => setLocalFlashcard({ ...localFlashcard, secondaryImage })}
                    />
                </div>
            </Flex>
        );
    };

    const renderDefinitionSection = () => {
        return (
            <Flex style={{ flexDirection: mdDown ? "column" : "row", gap: "inherit", width: "100%" }}>
                <div style={{ marginTop: "2rem", width: "100%", maxWidth: "70rem" }}>
                    <FlashcardEditor
                        key={localFlashcard.flashcardId}
                        defaultValue={localFlashcard.definition || ""}
                        handleInputChange={definition =>
                            setLocalFlashcard({ ...localFlashcard, definition, definitionAudio: null })
                        }
                        accentProps={{ isAccentButtonsVisible: false }}
                        flashcard={{
                            side: FlashcardSide.DEFINITION,
                            term: {
                                content: localFlashcard.term || "",
                                language: flashcardSet?.termLanguage || FLASHCARD_LANGUAGES.ENGLISH,
                            },
                            definition: {
                                content: localFlashcard.definition || "",
                                language: flashcardSet?.definitionLanguage || FLASHCARD_LANGUAGES.ENGLISH,
                            },
                            onChooseLanguage: selectedLang =>
                                updateFlashcardSetMetaData?.({ definitionLanguage: selectedLang }),
                        }}
                        // EDITOR DEFINITION SUGGESTION
                        editorProps={{
                            onCreate: ({ editor }) => {
                                definitionInputRef.current = editor;
                            },
                            onFocus: ({ editor }) => {
                                setFocusedEditor(editor);
                                updateCurrInputFocus({ definition: true });
                            },
                            onBlur: () => {
                                setFocusedEditor(null);
                                updateCurrInputFocus({ definition: false });
                            },
                            disableCopilot: flashcardSet?.autocompleteSettings
                                ? !!flashcardSet?.autocompleteSettings?.disabled
                                : !!user?.autocompleteSettings?.disabled,
                            timeout:
                                flashcardSet?.autocompleteSettings?.speed || user?.autocompleteSettings?.speed || 2000,
                            onSuggestionFetch: async () => {
                                const term = termInputRef.current?.getText() || "";
                                const definition = definitionInputRef.current?.getText() || "";

                                if (!term && !definition) return "";

                                const { flashcards, title } = await getAIFlashcardsContext({
                                    flashcardSetId: localFlashcard.flashcardSetId as string,
                                    currFlashcardIdx: 0,
                                });

                                const suggestion = await getSuggestion({
                                    type: AICompletionType.FLASHCARD_AUTOCOMPLETE,
                                    aiParams: {
                                        type: !definition
                                            ? FlashcardAutocompleteType.AUTOCOMPLETE_DEFINITION_FROM_TERM
                                            : FlashcardAutocompleteType.AUTOCOMPLETE_DEFINITION,
                                        context: flashcards,
                                        topic: title,
                                        term,
                                        definition,
                                        messages: [],
                                    },
                                });

                                return suggestion;
                            },
                            onAlternateSuggestionFetch: async () => {
                                const term = termInputRef.current?.getText() || "";
                                const definition = definitionInputRef.current?.getText() || "";

                                if (!term && !definition) return "";

                                const { flashcards, title } = await getAIFlashcardsContext({
                                    flashcardSetId: localFlashcard.flashcardSetId as string,
                                    currFlashcardIdx: 0,
                                });

                                const suggestion = await getSuggestion({
                                    type: AICompletionType.FLASHCARD_AUTOCOMPLETE,
                                    aiParams: {
                                        type: !definition
                                            ? FlashcardAutocompleteType.REVISION_DEFINITION_FROM_TERM
                                            : FlashcardAutocompleteType.REVISION_DEFINITION,
                                        context: flashcards,
                                        topic: title,
                                        term,
                                        definition,
                                        messages: [],
                                    },
                                });

                                return suggestion;
                            },
                            onSuggestionAccept: ({ state }) => {
                                logAutoCompleteSuggestionAcceptance({
                                    isAccepted: state,
                                });
                            },
                        }}
                        handleImagePaste={image => setLocalFlashcard({ ...localFlashcard, image })}
                    />
                </div>

                <FlexColumn style={{ margin: "auto", gap: spacing.XS, alignItems: "stretch" }}>
                    <ImageButton
                        image={localFlashcard.image}
                        onImageChange={async ({ image }) => setLocalFlashcard({ ...localFlashcard, image })}
                        style={{
                            wordBreak: "normal",
                        }}
                    />
                    <RecordButton
                        onClick={() => handleRecordButtonClick(FlashcardSide.DEFINITION)}
                        tabIndex={-1}
                        inAction={recordingSide === FlashcardSide.DEFINITION}
                        storedAudio={localFlashcard.definitionRecordedAudio}
                        isBlocked={isCustomizationFeaturesBlocked}
                        style={{
                            wordBreak: "normal",
                        }}
                    />
                </FlexColumn>
            </Flex>
        );
    };

    const renderHeader = () => {
        const TOOLBAR_HEIGHT = "3.8rem";

        return (
            <FlexRowAlignCenter style={{ justifyContent: "flex-end", position: "relative", minHeight: TOOLBAR_HEIGHT }}>
                <div
                    style={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}
                    onMouseDown={event => {
                        // to not blur the input while using the toolbar
                        event.preventDefault();
                    }}>
                    <FlashcardEditorToolbar editor={focusedEditor} style={{ height: TOOLBAR_HEIGHT }} />
                </div>
            </FlexRowAlignCenter>
        );
    };

    const renderBottomButtonsSection = () => {
        return (
            <FlexRowAlignCenter style={{ justifyContent: "flex-end", gap: spacing.XS, height: "2rem" }}>
                {isBottomSectionVisible && (
                    <>
                        {!isShowingCustomMCSection && (
                            <CustomizationFeatureButton
                                onClick={showCustomMCSection}
                                icon={<Settings2Icon size={16} color={themeColors.neutralBlack} />}
                                text={"Add multiple choice options"}
                                activeText={"Custom multiple choice on"}
                                isActive={!!localFlashcard.mcOptions}
                                isBlocked={isCustomizationFeaturesBlocked}
                            />
                        )}
                        <CustomizationFeatureButton
                            onClick={handleOpenCustomHintsPopup}
                            icon={<Lightbulb size={16} color={themeColors.neutralBlack} />}
                            text={"Add custom hints"}
                            isActive={!!localFlashcard.termCustomHint || !!localFlashcard.definitionCustomHint}
                            activeText={"Custom hints on"}
                            isBlocked={isCustomizationFeaturesBlocked}
                        />
                    </>
                )}
            </FlexRowAlignCenter>
        );
    };

    const renderAudioSection = () => {
        return (
            <LazyLoaded load={isShowingAudioSection}>
                <AudioInputSection
                    flashcard={localFlashcard}
                    definitionKnowtAudio={localFlashcard.definitionAudio}
                    isHidden={!isShowingAudioSection}
                    recordingSide={recordingSide}
                    getAudioSideReady={(side: FlashcardSide) => setRecordingSide(side)}
                    termAudio={localFlashcard.termRecordedAudio}
                    definitionAudio={localFlashcard.definitionRecordedAudio}
                    onChange={handleUpdateLocalAudio}
                />
            </LazyLoaded>
        );
    };

    const renderCustomMCSection = () => {
        return (
            <LazyLoaded load={isShowingCustomMCSection}>
                <CustomMcOptionsInputSection
                    flashcardId={localFlashcard.flashcardId}
                    isHidden={!isShowingCustomMCSection}
                    localMcOptions={localMcOptions}
                    onChange={handleUpdateLocalMcOptions}
                    onRemoveOptions={() => setIsShowingCustomMCSection(false)}
                />
            </LazyLoaded>
        );
    };

    const renderCustomHintsPopup = () => {
        return (
            <LazyLoaded load={isCustomHintPopupOpen}>
                <CustomHintPopup
                    isOpen={isCustomHintPopupOpen}
                    onClose={() => setIsCustomHintPopupOpen(false)}
                    defaultTermHint={localTermCustomHint}
                    defaultDefinitionHint={localDefinitionCustomHint}
                    termLanguage={termLanguage}
                    defLanguage={definitionLanguage}
                    onSave={handleSaveCustomHints}
                />
            </LazyLoaded>
        );
    };

    const renderContent = () => (
        <FlexColumn
            style={{ gap: "4rem" }}
            onKeyDown={e => {
                e.stopPropagation();
            }}>
            {renderHeader()}
            <Flex style={{ gap: "2rem", flexDirection: mdDown ? "column" : "row", wordBreak: "break-word" }}>
                {renderTermSection()}
                {renderDefinitionSection()}
            </Flex>
            {renderBottomButtonsSection()}
            {renderAudioSection()}
            {renderCustomMCSection()}
            {renderCustomHintsPopup()}
        </FlexColumn>
    );

    return (
        <ResponsiveDialog
            isOpen={isOpen}
            onClose={handleClose}
            disableEscapeKeyDown
            disableBackdropClick
            DialogStyle={{ borderRadius: "2rem", padding: "3.5rem", width: "80%" }}
            SlideMenuStyle={{ padding: "2.47rem 2rem 2rem 2rem" }}>
            <DialogCloseBtn style={{ marginLeft: "auto" }} onClick={handleClose} />
            {renderContent()}
        </ResponsiveDialog>
    );
};

export default EditableFlashcardPopup;
